/**
 * AdsAlerts — shared pieces of the proactive Google Ads alerts surface.
 *
 * The googleAdsAlerts collection holds one doc per (account, rule) — see
 * functions/src/functions/http/google-ads-alerts.ts. Two consumers:
 *
 *  - PortfolioDashboard: collapsed summary strip + per-row severity chips,
 *    expandable into <AlertRows> for cross-account triage.
 *  - AccountAlertsPanel (below): the full alert list for ONE account, on the
 *    account page — where the context to actually act on an alert lives.
 *
 * Ack is a direct client write (status: 'acknowledged') — permitted by the
 * firestore.rules catch-all. Re-detection preserves the ack while the
 * condition keeps firing and flips to 'resolved' when it clears.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, doc, query as fsQuery, where, updateDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../firebase';

export interface AlertDoc {
  id: string;
  accountId?: string;
  accountName?: string;
  type?: string;
  severity?: string;
  title?: string;
  detail?: string;
  status?: string;
  /** Written by the detection engine. Firestore Timestamps — NOT the plain
   *  epoch number `ackAt` uses, hence the tolerant reader below. */
  firstDetectedAt?: any;
  lastDetectedAt?: any;
}

/** Firestore Timestamp | epoch ms | ISO string → epoch ms. */
function toMillis(v: any): number | null {
  if (!v) return null;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  const nn = typeof v === 'number' ? v : Date.parse(String(v));
  return isFinite(nn) ? nn : null;
}

const NEW_ALERT_MS = 24 * 60 * 60 * 1000;

/** How long this condition has been firing. null when the engine hasn't
 *  stamped it (older docs). */
export function alertAgeMs(a: AlertDoc): number | null {
  const ms = toMillis(a.firstDetectedAt);
  return ms === null ? null : Date.now() - ms;
}

export function isNewAlert(a: AlertDoc): boolean {
  const age = alertAgeMs(a);
  return age !== null && age < NEW_ALERT_MS;
}

/** Compact age label: how long the alert has been standing. */
export function formatAlertAge(a: AlertDoc): string | null {
  const age = alertAgeMs(a);
  if (age === null) return null;
  const mins = Math.floor(age / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

/** Active first, then NEW first (a condition that started today is the one worth
 *  looking at), then severity high → low, then oldest-standing first within a
 *  tier — a three-week-old alert and one that fired an hour ago used to be
 *  indistinguishable. */
export function sortAlerts(alerts: AlertDoc[]): AlertDoc[] {
  const sev: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const st: Record<string, number> = { active: 0, acknowledged: 1 };
  return alerts.slice().sort((a, b) => {
    const sa = st[a.status || 'active'] ?? 0; const sb = st[b.status || 'active'] ?? 0;
    if (sa !== sb) return sa - sb;
    const na = isNewAlert(a) ? 0 : 1; const nb = isNewAlert(b) ? 0 : 1;
    if (na !== nb) return na - nb;
    const dsev = (sev[a.severity || 'medium'] ?? 1) - (sev[b.severity || 'medium'] ?? 1);
    if (dsev !== 0) return dsev;
    return (alertAgeMs(b) ?? 0) - (alertAgeMs(a) ?? 0);
  });
}

export async function ackAlert(id: string): Promise<void> {
  try { await updateDoc(doc(db, 'googleAdsAlerts', id), { status: 'acknowledged', ackAt: Date.now() }); }
  catch (e: any) { toast.error(`Couldn't acknowledge: ${e?.message || 'error'}`); }
}

export async function unackAlert(id: string): Promise<void> {
  try { await updateDoc(doc(db, 'googleAdsAlerts', id), { status: 'active', ackAt: null }); }
  catch (e: any) { toast.error(`Couldn't reactivate: ${e?.message || 'error'}`); }
}

/** Presentational alert rows. The account-name button renders only when an
 *  onSelectAccount handler is provided (portfolio); on the account page every
 *  row already IS the account, so it's omitted. */
/** Alert types this UI can route an "open" action for (the rest stay
 *  informational). Mirrors the detection-engine keys. */
const ACTIONABLE_TYPES = new Set(['wasted_terms', 'ads_disapproved', 'budget_lost', 'spend_no_conv', 'conv_drop', 'spend_spike', 'spend_drop']);

/** Which deep-view tab answers each alert type. Lives here beside
 *  ACTIONABLE_TYPES so the portfolio and the account page route identically —
 *  the account page owns the richer in-page routing (it can also pre-filter and
 *  open modals); this is the cross-account entry point. */
export function focusTabForAlert(type: string | undefined): string | undefined {
  switch (String(type || '')) {
    case 'wasted_terms': return 'searchTerms';
    case 'ads_disapproved': return 'ads';
    case 'spend_no_conv':
    case 'conv_drop': return 'conversions';
    case 'budget_lost': return 'campaigns';
    default: return undefined;
  }
}

export const AlertRows: React.FC<{
  alerts: AlertDoc[];
  onSelectAccount?: (customerId: string) => void;
  /** Account page: jump to the place this alert is acted on. */
  onAct?: (a: AlertDoc) => void;
}> = ({ alerts, onSelectAccount, onAct }) => (
  <>
    {alerts.map((a) => {
      const canAct = !!onAct && ACTIONABLE_TYPES.has(String(a.type || ''));
      return (
      <div
        key={a.id}
        className={`ads-portfolio__alert ads-portfolio__alert--${a.severity || 'medium'}${a.status === 'acknowledged' ? ' ads-portfolio__alert--ack' : ''}${canAct ? ' ads-portfolio__alert--clickable' : ''}`}
        onClick={canAct ? () => onAct!(a) : undefined}
        role={canAct ? 'button' : undefined}
        tabIndex={canAct ? 0 : undefined}
        onKeyDown={canAct ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAct!(a); } } : undefined}
        title={canAct ? 'Open — review and act on this' : undefined}
      >
        <span className="ads-portfolio__alert-sev">{a.severity || 'medium'}</span>
        {onSelectAccount && (
          <button
            type="button"
            className="ads-portfolio__alert-acct"
            onClick={(e) => { e.stopPropagation(); a.accountId && onSelectAccount(a.accountId); }}
            title="Open account"
          >
            {a.accountName || a.accountId}
          </button>
        )}
        <span className="ads-portfolio__alert-title">{a.title}</span>
        {isNewAlert(a) && <span className="ads-portfolio__alert-new" title="First detected in the last 24 hours">new</span>}
        <span className="ads-portfolio__alert-detail">{a.detail}</span>
        {(() => {
          const age = formatAlertAge(a);
          if (!age) return null;
          return (
            <span className="ads-portfolio__alert-age" title="How long this condition has been firing">
              {age}
            </span>
          );
        })()}
        {canAct && <span className="ads-portfolio__alert-open" aria-hidden="true">open →</span>}
        {a.status === 'acknowledged'
          ? <button type="button" className="ads-portfolio__alert-ack" onClick={(e) => { e.stopPropagation(); void unackAlert(a.id); }} title="Move back to active">[un-ack]</button>
          : <button type="button" className="ads-portfolio__alert-ack" onClick={(e) => { e.stopPropagation(); void ackAlert(a.id); }} title="Acknowledge — hides it from the active list">[ack]</button>}
      </div>
      );
    })}
  </>
);

/** Self-subscribing alerts panel for ONE account (the account page).
 *  Renders nothing when the account has no active/acknowledged alerts.
 *  Acknowledged alerts are collapsed behind a toggle so a clean account reads
 *  calm; active alerts are always shown and (via onAct) click through to the
 *  place they're acted on. */
export const AccountAlertsPanel: React.FC<{
  customerId: string;
  onAct?: (a: AlertDoc) => void;
}> = ({ customerId, onAct }) => {
  const [alerts, setAlerts] = useState<AlertDoc[]>([]);
  const [showAcked, setShowAcked] = useState(false);

  useEffect(() => {
    setAlerts([]);
    setShowAcked(false);
    // Equality-only filter (no composite index needed); status filtered
    // client-side — an account has at most one doc per rule (~7 docs).
    const unsub = onSnapshot(
      fsQuery(collection(db, 'googleAdsAlerts'), where('accountId', '==', customerId)),
      (snap) => setAlerts(
        snap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((a: AlertDoc) => a.status === 'active' || a.status === 'acknowledged'),
      ),
      (err) => { console.warn('[AccountAlertsPanel] alerts sub error:', err); },
    );
    return () => unsub();
  }, [customerId]);

  const active = useMemo(() => sortAlerts(alerts.filter((a) => a.status === 'active')), [alerts]);
  const acked = useMemo(() => sortAlerts(alerts.filter((a) => a.status === 'acknowledged')), [alerts]);

  if (active.length === 0 && acked.length === 0) return null;

  const calm = active.length === 0;
  return (
    <div className={`ads-portfolio__alerts${calm ? ' ads-portfolio__alerts--calm' : ''}`}>
      <div className="ads-portfolio__alerts-head">
        {calm
          ? <>✓ no active alerts on this account</>
          : <>⚠ {active.length} active alert{active.length === 1 ? '' : 's'} on this account</>}
        {acked.length > 0 && (
          <button
            type="button"
            className="ads-portfolio__alerts-toggle"
            onClick={() => setShowAcked((v) => !v)}
            title={showAcked ? 'Hide acknowledged alerts' : 'Show acknowledged alerts'}
          >
            {' · '}{acked.length} acknowledged {showAcked ? '[hide]' : '[show]'}
          </button>
        )}
      </div>
      {active.length > 0 && <AlertRows alerts={active} onAct={onAct} />}
      {showAcked && acked.length > 0 && <AlertRows alerts={acked} onAct={onAct} />}
    </div>
  );
};
